import React, { useState, useEffect, useRef } from 'react';
import { createClient } from '@supabase/supabase-js';
import './MessageSystem.css';
import { useIsMobileOrTablet } from "./Util/isMobileOrTablet";
import CanvasDraw from "react-canvas-draw-v2";

// Supabase client setup
const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseKey = process.env.REACT_APP_SUPABASE_KEY;
const supabaseClient = createClient(supabaseUrl, supabaseKey);

const MessageSystem = () => {
  const [messages, setMessages] = useState([]);
  const [username, setUsername] = useState('');
  const [message, setMessage] = useState('');
  const [selectedChannel, setSelectedChannel] = useState('info');
  const [channels, setChannels] = useState([
    { id: 'announcements', channelname: '#announcements', locked: true },
    { id: 'info', channelname: '#info', locked: true },
    { id: 'drawing', channelname: '#drawing', locked: true },
    { id: 'general', channelname: '#general', locked: false },
    { id: 'kasane-teto', channelname: '#kasane-teto', locked: false },
  ]);
  const [lastImage, setLastImage] = useState(null);
  const UID = React.useMemo(() => new Date().getTime().toString(16) + Math.floor(Math.random() * 1000).toString(16), []);

  // Reference for message box container
  const messageBoxRef = useRef(null);
  const prevMessageCount = useRef(0); // Track the previous number of messages

  // Fetch messages from Supabase (all go to the selected channel)
  const fetchMessages = async () => {
    try {
        // if channel is "'Unknown'::text", then its general channel (yes thats the string)
      var { data, error } = await supabaseClient
        .from('messages')
        .select('*')
        .order('created_at', { ascending: true });

      for (let i = 0; i < data.length; i++) {
        if (data[i].channel === "'Unknown'::text") {
            data[i].channel = 'general';
        }
      }

      // only use messages from the selected channel
        data = data.filter((msg) => msg.channel === selectedChannel);

      if (error) throw error;
      setMessages(data.slice(-24)); // Keep only the last 24 messages
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  };

  // Post message to Supabase
  const sendMessage = async () => {
    if (!message.trim()) {
      alert('Please fill in the message field.');
      return;
    }

    const usernameToUse = username || `Anonymous #${UID}`;
    try {
      await supabaseClient.from('messages').insert([{
        username: usernameToUse,
        message,
        channel: selectedChannel,
      }]);

      // send to process.env.REACT_APP_MESSAGE_WEBHOOK
        // remove @everyone to @.everyone
        var newMessage = message.replace(/@everyone/g, "@.everyone");
        fetch(process.env.REACT_APP_MESSAGE_WEBHOOK, {
            method: 'POST',
            headers: {
            'Content-Type': 'application/json',
            },
            body: JSON.stringify({
            content: `**${usernameToUse}**: ${newMessage}\n\nPosted on ${new Date().toLocaleString()} in ${channels.find((channel) => channel.id === selectedChannel)?.channelname}`,
            }),
        });
      setMessage(''); // Clear the input field
      fetchMessages(); // Refresh messages
    } catch (error) {
      console.error('Error sending message:', error);
    }
  };

  // Scroll to the bottom only if a new message is added
  useEffect(() => {
    if (messageBoxRef.current && messages.length > prevMessageCount.current) {
      messageBoxRef.current.scrollTop = messageBoxRef.current.scrollHeight;
    }
    prevMessageCount.current = messages.length; // Update previous message count
  }, [messages]);

  useEffect(() => {
    fetchMessages();
    const interval = setInterval(fetchMessages, 500); // Poll messages every 500ms
    return () => clearInterval(interval); // Cleanup on unmount
  }, [selectedChannel]); // Fetch messages when the channel changes

  const handleSend = () => {
    if (message.trim()) {
      sendMessage();
    }
  };

  // create a reference of a CanvasDraw component
  const canvasRef = useRef(null);

  if (useIsMobileOrTablet()) {
    return (
      <div className="m-message-box">
      {/* CanvasDraw component */}
      <CanvasDraw
        ref={canvasRef}
        hideGrid
        brushRadius={canvasRef.current?.ctx.drawing.lineWidth || 2}
        canvasWidth={500}
        canvasHeight={500}
        lazyRadius={0}
        brushColor={canvasRef.current?.ctx.drawing.fillStyle || "#000"}
        className="m-canvas-container"
      />

      <div className="m-controls">
        <button
          onClick= {(e) => {
            const canvas = document.createElement("canvas");
            canvas.width = 500;
            canvas.height = 500;
            const ctx = canvas.getContext("2d");
            const img = new Image();
            img.src = canvasRef.current.ctx.drawing.canvas.toDataURL("image/png");
            
            img.onload = () => {
              // set bg color
              ctx.fillStyle = "#fff";
              ctx.fillRect(0, 0, 500, 500);
              
              ctx.drawImage(img, 0, 0, 500, 500);

              canvas.toBlob(function(blob) {
                  var reader = new FileReader();
                  reader.onload = function() {
                      var base64 = reader.result.split(",")[1];
                      console.log(base64);
                      if (base64 === lastImage) {
                          alert("You've already sent this drawing!");
                          return;
                      } else {
                          var formData = new FormData();

                          alert("Sent drawing!");

                          formData.append("file", blob, "drawing.png");
                          formData.append("content", "Check out this drawing! (Posted on " + new Date().toLocaleString() + ")");

                          fetch(process.env.REACT_APP_IMAGE_WEBHOOK, {
                              method: 'POST',
                              body: formData
                          })
                          .then(response => {
                              if (response.ok) {
                                  console.log("Image sent successfully!");
                                  setLastImage(base64);
                              } else {
                                  console.error("Failed to send image:", response.statusText);
                              }
                          })
                          .catch(error => {
                              console.error("Error sending image:", error);
                          });
                      }
                  };
                  reader.readAsDataURL(blob);
                }, "image/png");
              }
            }
          }
          className="m-button m-button-send"
        >
          Send
        </button>
        <button
          onClick={(e) => canvasRef.current.clear()}
          className="m-button m-button-clear"
        >
          Clear
        </button>
        <button
          onClick={(e) => canvasRef.current.undo()}
          className="m-button m-button-undo"
        >
          Undo
        </button>

        <div className="m-color-brush-container">
          <input
            type="color"
            id="color-picker"
            onChange= {(e) => {
              canvasRef.current.ctx.drawing.strokeStyle = e.target.value;
              canvasRef.current.ctx.drawing.fillStyle = e.target.value;
            }}
            className="m-color-picker"
          />
          <input
            type="range"
            id="brush-size"
            min="1"
            max="10"
            defaultValue="1"
            onChange= {(e) => {
              canvasRef.current.ctx.drawing.lineWidth = e.target.value;
            }}
            className="m-brush-size"
          />
        </div>
      </div>
    </div>
    );
  }

  // We
  return (
    <div className="message-system">
      <div className="left-sidebar">
        <div className="channel-list">
          {
            channels.map((channel) => (
                <div
                key={channel.id}
                className={`channel-button ${selectedChannel === channel.id ? 'active' : ''}`}
                onClick={() => setSelectedChannel(channel.id)}
                >
                {channel.channelname} {channel.locked && <span className="locked">[Locked]</span>}
                </div>
            ))
          }
        </div>
      </div>

      <div className="main-content">
        <div className="message-box" ref={messageBoxRef}>
        {selectedChannel === 'drawing' && (
          <div className="d-message-box">
            <div className="d-canvas-container">
              <CanvasDraw
                ref={canvasRef}
                hideGrid
                brushRadius={canvasRef.current?.ctx.drawing.lineWidth || 2}
                canvasWidth={500}
                canvasHeight={500}
                lazyRadius={0}
                brushColor={canvasRef.current?.ctx.drawing.fillStyle || "#000"}
              />
            </div>

            {/* Drawing functions */}
            <div className="d-controls">
              <button onClick={(e) => { 
                const canvas = document.createElement("canvas");
                canvas.width = 500;
                canvas.height = 500;
                const ctx = canvas.getContext("2d");
                const img = new Image();
                
                img.src = canvasRef.current.ctx.drawing.canvas.toDataURL("image/png");
                img.onload = () => {
                    // set bg color
                    ctx.fillStyle = "#fff";
                    ctx.fillRect(0, 0, 500, 500);
                    
                    ctx.drawImage(img, 0, 0, 500, 500);

                    alert("Sent image!");

                    canvas.toBlob(function(blob) {
                      var reader = new FileReader();
                      reader.onload = function() {
                          var base64 = reader.result.split(",")[1];
                          console.log(base64);
                          if (base64 === lastImage) {
                              alert("You've already sent this drawing!");
                              return;
                          } else {
                              var formData = new FormData();
                              formData.append("file", blob, "drawing.png");
                              formData.append("content", "Check out this drawing! (Posted on " + new Date().toLocaleString() + ")");

                              fetch(process.env.REACT_APP_IMAGE_WEBHOOK, {
                                  method: 'POST',
                                  body: formData
                              })
                              .then(response => {
                                  if (response.ok) {
                                      console.log("Image sent successfully!");
                                      setLastImage(base64);
                                  } else {
                                      console.error("Failed to send image:", response.statusText);
                                  }
                              })
                              .catch(error => {
                                  console.error("Error sending image:", error);
                              });
                          }
                      };
                      reader.readAsDataURL(blob);
                    }, "image/png");
                  }
                }
               } className="d-button d-button-send">
                Send
              </button>
              <button onClick={() => { canvasRef.current.clear(); }} className="d-button d-button-clear">
                Clear
              </button>
              <button onClick={() => { canvasRef.current.undo(); }} className="d-button d-button-undo">
                Undo
              </button>

              <div className="d-input-container">
                Color Picker
                <input
                  type="color"
                  id="color-picker"
                  onChange={(e) => {
                    canvasRef.current.ctx.drawing.strokeStyle = e.target.value;
                    canvasRef.current.ctx.drawing.fillStyle = e.target.value;
                  }}
                  className="d-color-picker"
                />
              </div>

              <div className="d-input-container">
                Brush Size
                <input
                  type="range"
                  id="brush-size"
                  min="1"
                  max="10"
                  defaultValue="1"
                  onChange={(e) => {
                    canvasRef.current.ctx.drawing.lineWidth = e.target.value;
                  }}
                  className="d-brush-size"
                />
              </div>
            </div>
          </div>
        )
        || (
              messages.map((msg, index) => {
                  const date = new Date(msg.created_at);
                  // get users timezone offset
                  const timezoneOffset = date.getTimezoneOffset();
                  // convert the date to UTC
                  date.setMinutes(date.getMinutes() + timezoneOffset);
                  return (
                      <div key={index} className="message-container">
                      <div className="message">{msg.message}</div>
                      <div className="username">
                          - {msg.username} ({date.toLocaleString()})
                      </div>
                      </div>
                  );
                  })
              )
          }
        </div>

        {/* Fixed Input Section at Bottom */}
        {
          selectedChannel !== 'drawing' && (
            <div className="input-section">
                <input
                  type="text"
                  className="user-input"
                  placeholder="Enter your username (optional)"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                />
                <input
                  type="text"
                  className="message-input"
                  placeholder="Enter your message"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSend()}
                  disabled={channels.find((channel) => channel.id === selectedChannel)?.locked} // Disable input for locked channels
                />
                <button
                  className="message-send"
                  onClick={handleSend}
                  disabled={channels.find((channel) => channel.id === selectedChannel)?.locked} // Disable the send button for locked channels
                >
                  Send
                </button>
            </div>
          ) || null
        }
      </div>
    </div>
  );
};

export default MessageSystem;
